import { useContext, useEffect, useRef, useState } from "react";
import { useLogin, useRedirect } from "react-admin";
import loginAvatar from "./../static/login-avatar.png";
// import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
// import Card from "@mui/material/Card";
import { useForm, SubmitHandler } from "react-hook-form";
import NotificationMessage, {
  notificationType,
} from "../components/NotificationMessage/NotificationMessage";
import { routePaths } from "./SidebarMenu";
import FdTextField from "../components/UIComponents/TextField/FdTextfield";
import FdButton from "../components/UIComponents/Button/FdButton";
import FdAvatar from "../components/UIComponents/Avatar/FdAvatar";
import FdTypography from "../components/UIComponents/Typography/FdTypography";
import FdCard from "../components/UIComponents/Card/FdCard";
import { theme } from "../theme";
import { signIn as classes } from "./styles";
import { APIContext } from "../store/APIStore";
import { IAuthResp } from "../dataProvider/interfaces/IUser";
import fdAuthProvider from "../authProvider/authProvider";
import { Divider } from "@mui/material";
import GoogleIcon from "../components/Svg/GoogleIcon";
import posthog from "posthog-js";

interface FormData {
  username: string;
  password: string;
}
interface FormProps {
  initialValues: FormData;
  onSuccess?: Function;
}

const MESSAGES = {
  ERROR_SERVER:
    "We are facing some technical issue, please try after some time.",
  INVALID_CREDS: "Please try again with the valid user credentials",
};

const SignInForm = (props: FormProps) => {
  const { initialValues, onSuccess = () => {} } = props;
  const redirect = useRedirect();
  const api = useContext(APIContext);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: initialValues,
  });
  const reactAdminLogin = useLogin();
  const notificationRef = useRef<any>();

  useEffect(() => {
    fdAuthProvider.logout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit: SubmitHandler<FormData> = async (data) => {
    try {
      posthog?.capture("login_btn_click", { user: data.username });
      const resp: Response = await api.userService.loginV1(data);
      if (resp.status === 200) {
        posthog?.capture("login_success", { user: data.username });
        const auth: IAuthResp = await resp.json();
        await reactAdminLogin(auth);
        redirect(`/${routePaths.ACTIVE_EVENTS}`);
      } else if (resp.status === 409) {
        posthog?.capture("login_failed", {
          cause: "two_fa_required",
          user: data.username,
        });
        onSuccess({ key: "two_fa_required", data });
      } else if (resp.status >= 500) {
        throw new Error("ERROR_SERVER", { cause: "SERVER" });
      } else {
        throw new Error(resp.statusText);
      }
    } catch (error: any) {
      console.log(error);
      fdAuthProvider.logout();
      const msg = error?.message?.includes("ERROR_SERVER")
        ? MESSAGES.ERROR_SERVER
        : MESSAGES.INVALID_CREDS;
      notificationRef.current?.show(msg, notificationType.Error);
      posthog?.capture("login_failed", {
        cause: msg,
        user: data.username,
      });
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box
          mt={2}
          sx={{
            "& .MuiFormControl-root": {
              mb: 0.5,
            },
            "& .MuiInputBase-root": {
              m: 0,
            },
            "& .MuiFormHelperText-root ": {
              mx: 0,
            },
          }}
        >
          <FdTextField
            register={register}
            name="username"
            id="username"
            label="Username"
            placeholder="Enter username"
            error={errors.username?.type === "required"}
            errorMsg={errors.username && "Username is required"}
            required
          />
        </Box>
        <Box mb={3}>
          <FdTextField
            register={register}
            name="password"
            id="password"
            label="Password"
            placeholder="Enter password"
            type="password"
            error={errors.password?.type === "required"}
            errorMsg={errors.password && "Password is required"}
            required
          />
        </Box>
        <Box>
          <FdButton
            type="submit"
            variant="contained"
            color="primary"
            size="medium"
            ButtonName="Login"
            fullWidth
          />
        </Box>
      </form>
      <NotificationMessage ref={notificationRef} />
    </>
  );
};

const SignIn = () => {
  const redirect = useRedirect();
  const reactAdminLogin = useLogin();
  const [loginResp, setLoginResp] = useState<any>(null);
  const popupRef = useRef<any>(null);
  const api = useContext(APIContext);
  const notificationRef = useRef<any>();
  const [user, setUser] = useState<string>("");

  const updateLoginResp = ({ key, data }: any) => {
    const UI_CONFIG: any = {
      default: {
        isDefault: true,
        heading: "Welcome to the fielddrive portal",
        text: "Please login to continue",
        link: "Forgot password?",
        onLinkClick: () => redirect(`/${routePaths.PASSWORD_FORGOT}`),
      },
      two_fa_required: {
        heading: `Welcome back ${data?.username}`,
        text: "Google sign-in is required for your user role to complete login.",
        link: "Login with other username?",
        onLinkClick: () => updateLoginResp({ key: "default" }),
        supportLinkTitle: "Need help? Contact",
        supportLink: "Support@fielddrive.com",
        onSupportLinkClick: () => {
          window.open(
            "mailto:support@fielddrive.com",
            "_blank",
            "noopener,noreferrer"
          );
        },
      },
      invalid_email: {
        heading: (
          <div>
            <span style={{ display: "block" }}>Oops!</span>
            <span>Something went wrong</span>
          </div>
        ),
        text: "We couldn't find an email address linked to your Google account. Please check your account settings and try again.",
        link: "Login with other username?",
        onLinkClick: () => updateLoginResp({ key: "default" }),
        supportLinkTitle: "Need help? Contact",
        supportLink: "Support@fielddrive.com",
        onSupportLinkClick: () => {
          window.open(
            "mailto:support@fielddrive.com",
            "_blank",
            "noopener,noreferrer"
          );
        },
      },
      unauthorized_user: {
        heading: "You're not registered",
        text: "The Google account you used isn't linked to any active user in our system. Please try logging in with a different account.",
        link: "Login with other username?",
        onLinkClick: () => updateLoginResp({ key: "default" }),
        supportLinkTitle: "Need help? Contact",
        supportLink: "Support@fielddrive.com",
        onSupportLinkClick: () => {
          window.open(
            "mailto:support@fielddrive.com",
            "_blank",
            "noopener,noreferrer"
          );
        },
      },
      invalid_email_domain: {
        heading: "Invalid email domain",
        text: '"Superuser" or "System-admin" user role access requires a verified fielddrive email. Please sign in with an authorized email address.',
        link: "Login with other username?",
        onLinkClick: () => updateLoginResp({ key: "default" }),
        supportLinkTitle: "Need help? Contact",
        supportLink: "Support@fielddrive.com",
        onSupportLinkClick: () => {
          window.open(
            "mailto:support@fielddrive.com",
            "_blank",
            "noopener,noreferrer"
          );
        },
      },
      unauthorized_origin: {
        heading: "Unauthorized origin",
        text: "Your login request came from an unrecognized source. Please ensure you're accessing the fdPortal from an approved domain.",
        link: "Login with other username?",
        onLinkClick: () => updateLoginResp({ key: "default" }),
        supportLinkTitle: "Need help? Contact",
        supportLink: "Support@fielddrive.com",
        onSupportLinkClick: () => {
          window.open(
            "mailto:support@fielddrive.com",
            "_blank",
            "noopener,noreferrer"
          );
        },
      },
      invalid_origin: {
        heading: "Invalid origin",
        text: "We couldn't verify the source of this request. Please try again from a supported browser or network.",
        link: "Login with other username?",
        onLinkClick: () => updateLoginResp({ key: "default" }),
        supportLinkTitle: "Need help? Contact",
        supportLink: "Support@fielddrive.com",
        onSupportLinkClick: () => {
          window.open(
            "mailto:support@fielddrive.com",
            "_blank",
            "noopener,noreferrer"
          );
        },
      },
    };

    const isExists = !!(key && UI_CONFIG[key]);
    if (isExists) {
      const config = UI_CONFIG[key];
      setLoginResp({ ...config });
    } else {
      notificationRef.current?.show(
        MESSAGES.ERROR_SERVER,
        notificationType.Error
      );
    }
  };

  const handleGoogleOauth = async () => {
    posthog?.capture("google_oauth_btn_click", { user });
    try {
      const url: string = api.userService.googleAuthUrlV1({
        provider: "google",
        origin: window.origin,
      });
      posthog?.capture("google_oauth_url_fetched", { user });
      popupRef.current = window.open(url);
    } catch (error: any) {
      console.log(error);
      const cause = error?.message ?? "server_error";
      posthog?.capture("google_oauth_failed", { cause, user });
      updateLoginResp({ key: "server_error" });
    }
  };

  const loginSuccess = (e: { key: string; data: any }) => {
    setUser(e.data?.username ?? "");
    updateLoginResp(e);
  };

  useEffect(() => {
    updateLoginResp({ key: "default" });
    const handleMessage = (event: MessageEvent) => {
      // 1) ignore HMR / webpack messages
      if (
        typeof event.data.type === "string" &&
        event.data.type.startsWith("webpack")
      ) {
        return;
      }

      // 2) ignore anything not from your popup
      if (event.source !== popupRef.current) {
        return;
      }

      // 3) ensure it’s your auth payload
      if (typeof event.data !== "object") {
        console.warn("Received postMessage but no data:", event.data);
        return;
      }

      // 4) ensure it’s your auth payload
      if (event.data.errorCode || !event.data.token) {
        console.warn("Received postMessage but no token:", event.data);
        const cause = event.data.errorCode ?? "no_access_token";
        posthog?.capture("google_oauth_failed", { cause, user });
        updateLoginResp({ key: cause });
        return;
      }

      // now log in + redirect:
      const data: IAuthResp = event.data as IAuthResp;
      posthog?.capture("google_oauth_success", { user });
      reactAdminLogin(data).then(() => {
        window.removeEventListener("message", handleMessage);
        redirect(`/${routePaths.ACTIVE_EVENTS}`);
      });
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  return (
    <>
      <Box sx={classes.wrapper}>
        <FdCard sx={classes.card}>
          <FdAvatar src={loginAvatar} sx={classes.avatar} />
          <FdTypography
            sx={classes.headingDiv}
            variant="h2"
            component="h2"
            label={loginResp?.heading}
          />
          <Box mt={1}>
            <FdTypography
              sx={{ color: "#000000", fontSize: "12px" }}
              label={loginResp?.text}
            />
          </Box>

          <Box mt={2}>
            <FdButton
              sx={classes.googleBtn}
              type="submit"
              variant="outlined"
              ButtonName="Login with Google"
              startIcon={<GoogleIcon />}
              onClick={handleGoogleOauth}
            />
          </Box>

          {loginResp?.isDefault ? (
            <>
              <Divider
                sx={{ color: "#000000", fontSize: "10px" }}
                textAlign="center"
              >
                or login with username
              </Divider>
              <SignInForm
                initialValues={{ username: "", password: "" }}
                onSuccess={loginSuccess}
              />
            </>
          ) : (
            ""
          )}
          <Box mt={1}>
            <FdButton
              onClick={loginResp?.onLinkClick}
              sx={classes.linkBtn}
              variant="text"
              ButtonName={loginResp?.link}
            />
          </Box>
          {loginResp?.supportLinkTitle ? (
            <Box mt={1}>
              <FdTypography
                sx={{ color: "#000000", fontSize: "12px", marginTop: "20px" }}
                label={loginResp?.supportLinkTitle}
              />
              <FdButton
                onClick={loginResp?.onSupportLinkClick}
                sx={classes.linkBtn}
                variant="text"
                ButtonName={loginResp?.supportLink}
              />
            </Box>
          ) : (
            ""
          )}
        </FdCard>
      </Box>
      <NotificationMessage ref={notificationRef} />
    </>
  );
};

export default SignIn;
